import React, { useState, useRef, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown, faTrash } from '@fortawesome/free-solid-svg-icons';

interface PolygonCoordinates {
  x: number;
  y: number;
}

interface ImageLabel {
  text: string;
  coordinates: PolygonCoordinates[];
}

interface LabeledImageEditorProps {
  name?: string;
}

type DragMode = 'none' | 'draw' | 'move' | 'resize';
type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w';

export default function LabeledImageEditor({ name = 'icon_description' }: LabeledImageEditorProps) {
  const { watch, setValue } = useFormContext();

  const formData = watch(name) || { image: null, labels: [] };
  const image = formData.image;
  const imageUrl = image?.public_url || '';
  const labels: ImageLabel[] = formData.labels || [];

  const [dragMode, setDragMode] = useState<DragMode>('none');
  const [startPoint, setStartPoint] = useState<PolygonCoordinates | null>(null);
  const [currentRect, setCurrentRect] = useState<PolygonCoordinates[] | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [activeHandle, setActiveHandle] = useState<ResizeHandle | null>(null);
  const [dragStartCoords, setDragStartCoords] = useState<PolygonCoordinates[] | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);


  const setLabels = (newLabels: ImageLabel[]) => {
    setValue(`${name}.labels`, newLabels, { shouldDirty: true });
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
    setImageSize({ width: img.clientWidth, height: img.clientHeight });
  };

  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

  const getMousePos = useCallback((e: React.MouseEvent): PolygonCoordinates => {
    const rect = imageRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };

    const scaleX = naturalSize.width / imageSize.width || 1;
    const scaleY = naturalSize.height / imageSize.height || 1;

    return {
      x: clamp(Math.round((e.clientX - rect.left) * scaleX), 0, naturalSize.width),
      y: clamp(Math.round((e.clientY - rect.top) * scaleY), 0, naturalSize.height),
    };
  }, [naturalSize, imageSize]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target !== imageRef.current) return;
    const pos = getMousePos(e);
    setDragMode('draw');
    setStartPoint(pos);
    setCurrentRect([pos, pos]);
    setSelectedIndex(null);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const pos = getMousePos(e);

    if (dragMode === 'draw' && startPoint) {
      setCurrentRect([startPoint, pos]);
    } else if (dragMode === 'move' && selectedIndex !== null && startPoint && dragStartCoords) {
      const dx = pos.x - startPoint.x;
      const dy = pos.y - startPoint.y;

      const [p1, p2] = dragStartCoords;
      const boxWidth = p2.x - p1.x;
      const boxHeight = p2.y - p1.y;

      // Calculate new positions
      let newX1 = p1.x + dx;
      let newY1 = p1.y + dy;

      // Clamp to image bounds
      newX1 = clamp(newX1, 0, naturalSize.width - boxWidth);
      newY1 = clamp(newY1, 0, naturalSize.height - boxHeight);

      const newLabels = [...labels];
      newLabels[selectedIndex] = {
        ...newLabels[selectedIndex],
        coordinates: [
          { x: newX1, y: newY1 },
          { x: newX1 + boxWidth, y: newY1 + boxHeight },
        ],
      };
      setLabels(newLabels);
    } else if (dragMode === 'resize' && selectedIndex !== null && startPoint && dragStartCoords && activeHandle) {
      const [origP1, origP2] = dragStartCoords;
      const dx = pos.x - startPoint.x;
      const dy = pos.y - startPoint.y;

      let newP1 = { ...origP1 };
      let newP2 = { ...origP2 };

      switch (activeHandle) {
        case 'nw':
          newP1.x = clamp(origP1.x + dx, 0, origP2.x - 10);
          newP1.y = clamp(origP1.y + dy, 0, origP2.y - 10);
          break;
        case 'ne':
          newP2.x = clamp(origP2.x + dx, origP1.x + 10, naturalSize.width);
          newP1.y = clamp(origP1.y + dy, 0, origP2.y - 10);
          break;
        case 'sw':
          newP1.x = clamp(origP1.x + dx, 0, origP2.x - 10);
          newP2.y = clamp(origP2.y + dy, origP1.y + 10, naturalSize.height);
          break;
        case 'se':
          newP2.x = clamp(origP2.x + dx, origP1.x + 10, naturalSize.width);
          newP2.y = clamp(origP2.y + dy, origP1.y + 10, naturalSize.height);
          break;
        case 'n':
          newP1.y = clamp(origP1.y + dy, 0, origP2.y - 10);
          break;
        case 's':
          newP2.y = clamp(origP2.y + dy, origP1.y + 10, naturalSize.height);
          break;
        case 'w':
          newP1.x = clamp(origP1.x + dx, 0, origP2.x - 10);
          break;
        case 'e':
          newP2.x = clamp(origP2.x + dx, origP1.x + 10, naturalSize.width);
          break;
      }

      const newLabels = [...labels];
      newLabels[selectedIndex] = {
        ...newLabels[selectedIndex],
        coordinates: [
          { x: Math.min(newP1.x, newP2.x), y: Math.min(newP1.y, newP2.y) },
          { x: Math.max(newP1.x, newP2.x), y: Math.max(newP1.y, newP2.y) },
        ],
      };
      setLabels(newLabels);
    }
  };

  const handleMouseUp = () => {
    if (dragMode === 'draw' && currentRect) {
      const [p1, p2] = currentRect;

      // Clamp coordinates to image bounds
      const clampedP1 = {
        x: clamp(Math.min(p1.x, p2.x), 0, naturalSize.width),
        y: clamp(Math.min(p1.y, p2.y), 0, naturalSize.height),
      };
      const clampedP2 = {
        x: clamp(Math.max(p1.x, p2.x), 0, naturalSize.width),
        y: clamp(Math.max(p1.y, p2.y), 0, naturalSize.height),
      };

      const width = clampedP2.x - clampedP1.x;
      const height = clampedP2.y - clampedP1.y;

      if (width > 10 && height > 10) {
        const newLabel: ImageLabel = {
          text: '',
          coordinates: [clampedP1, clampedP2],
        };
        setLabels([...labels, newLabel]);
        setSelectedIndex(labels.length);
      }
    }

    setDragMode('none');
    setStartPoint(null);
    setCurrentRect(null);
    setActiveHandle(null);
    setDragStartCoords(null);
  };

  const startMove = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    const pos = getMousePos(e);
    setDragMode('move');
    setSelectedIndex(index);
    setStartPoint(pos);
    setDragStartCoords([...labels[index].coordinates]);
  };

  const startResize = (e: React.MouseEvent, index: number, handle: ResizeHandle) => {
    e.stopPropagation();
    const pos = getMousePos(e);
    setDragMode('resize');
    setSelectedIndex(index);
    setStartPoint(pos);
    setActiveHandle(handle);
    setDragStartCoords([...labels[index].coordinates]);
  };

  const updateLabelText = (index: number, text: string) => {
    const newLabels = [...labels];
    newLabels[index] = { ...newLabels[index], text };
    setLabels(newLabels);
  };

  const deleteLabel = (index: number) => {
    setLabels(labels.filter((_, i) => i !== index));
    setSelectedIndex(null);
  };

  const moveLabel = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= labels.length) return;

    const newLabels = [...labels];
    [newLabels[index], newLabels[newIndex]] = [newLabels[newIndex], newLabels[index]];
    setLabels(newLabels);
    setSelectedIndex(newIndex);
  };

  const scaleX = imageSize.width / naturalSize.width || 1;
  const scaleY = imageSize.height / naturalSize.height || 1;

  const getRectStyle = (coordinates: PolygonCoordinates[]) => {
    const [p1, p2] = coordinates;
    return {
      left: Math.min(p1.x, p2.x) * scaleX,
      top: Math.min(p1.y, p2.y) * scaleY,
      width: Math.abs(p2.x - p1.x) * scaleX,
      height: Math.abs(p2.y - p1.y) * scaleY,
    };
  };

  const handleSize = 8;

  const getResizeHandles = (coordinates: PolygonCoordinates[]) => {
    const [p1, p2] = coordinates;
    const width = Math.abs(p2.x - p1.x) * scaleX;
    const height = Math.abs(p2.y - p1.y) * scaleY;

    const handles: { handle: ResizeHandle; style: React.CSSProperties; cursor: string }[] = [
      { handle: 'nw', style: { left: -handleSize / 2, top: -handleSize / 2 }, cursor: 'nwse-resize' },
      { handle: 'ne', style: { left: width - handleSize / 2, top: -handleSize / 2 }, cursor: 'nesw-resize' },
      { handle: 'sw', style: { left: -handleSize / 2, top: height - handleSize / 2 }, cursor: 'nesw-resize' },
      { handle: 'se', style: { left: width - handleSize / 2, top: height - handleSize / 2 }, cursor: 'nwse-resize' },
      { handle: 'n', style: { left: width / 2 - handleSize / 2, top: -handleSize / 2 }, cursor: 'ns-resize' },
      { handle: 's', style: { left: width / 2 - handleSize / 2, top: height - handleSize / 2 }, cursor: 'ns-resize' },
      { handle: 'w', style: { left: -handleSize / 2, top: height / 2 - handleSize / 2 }, cursor: 'ew-resize' },
      { handle: 'e', style: { left: width - handleSize / 2, top: height / 2 - handleSize / 2 }, cursor: 'ew-resize' },
    ];

    return handles;
  };

  // Get cropped image style for a label's region
  const getCroppedImageStyle = (coordinates: PolygonCoordinates[], thumbnailSize: number): React.CSSProperties => {
    if (!naturalSize.width || !naturalSize.height) return {};

    const [p1, p2] = coordinates;
    const cropX = Math.min(p1.x, p2.x);
    const cropY = Math.min(p1.y, p2.y);
    const cropWidth = Math.abs(p2.x - p1.x);
    const cropHeight = Math.abs(p2.y - p1.y);

    // Calculate scale to fit the entire crop region into the thumbnail (may leave empty space)
    const scale = Math.min(thumbnailSize / cropWidth, thumbnailSize / cropHeight);
    const scaledWidth = naturalSize.width * scale;
    const scaledHeight = naturalSize.height * scale;

    // Calculate position to center the crop region
    const offsetX = -cropX * scale + (thumbnailSize - cropWidth * scale) / 2;
    const offsetY = -cropY * scale + (thumbnailSize - cropHeight * scale) / 2;

    return {
      width: scaledWidth,
      height: scaledHeight,
      objectFit: 'none' as const,
      objectPosition: `${offsetX}px ${offsetY}px`,
    };
  };

  if (!imageUrl) {
    return (
      <p style={{ fontSize: '0.75rem', opacity: 0.6 }}>
        Select an image above to draw label regions.
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {imageUrl && (
        <div style={{ display: 'flex', gap: '12px', flexDirection: 'row', flexWrap: 'wrap' }}>
          {/* Image Canvas */}
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <div
              ref={containerRef}
              style={{
                position: 'relative',
                display: 'inline-block',
                userSelect: 'none',
                cursor: dragMode === 'none' ? 'crosshair' : 'default',
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <img
                ref={imageRef}
                src={imageUrl}
                alt="Label target"
                style={{ maxWidth: '100%', maxHeight: '350px', cursor: 'crosshair' }}
                onLoad={handleImageLoad}
                draggable={false}
              />

              {/* Draw instruction overlay - shown when no labels */}
              {labels.length === 0 && dragMode === 'none' && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      color: 'white',
                      padding: '12px 20px',
                      textAlign: 'center',
                      border: '2px dashed #f59e0b',
                    }}
                  >
                    <div style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '4px' }}>
                      Click and drag to draw regions
                    </div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                      Draw rectangles over areas you want to label
                    </div>
                  </div>
                </div>
              )}

              {/* Existing labels */}
              {labels.map((label, index) => {
                const style = getRectStyle(label.coordinates);
                const isSelected = selectedIndex === index;
                const handles = isSelected ? getResizeHandles(label.coordinates) : null;

                return (
                  <div
                    key={index}
                    style={{
                      position: 'absolute',
                      transition: dragMode === 'none' ? 'all 0.2s' : 'none',
                      zIndex: isSelected ? 20 : 10,
                      left: style.left,
                      top: style.top,
                      width: style.width,
                      height: style.height,
                      border: isSelected
                        ? '2px solid #fbbf24'
                        : '2px solid rgba(251, 191, 36, 0.6)',
                      boxShadow: isSelected
                        ? '0 0 15px rgba(251, 191, 36, 0.5), inset 0 0 15px rgba(251, 191, 36, 0.1)'
                        : '0 0 8px rgba(251, 191, 36, 0.3)',
                      background: isSelected
                        ? 'rgba(251, 191, 36, 0.15)'
                        : 'rgba(251, 191, 36, 0.05)',
                      cursor: isSelected ? 'move' : 'pointer',
                    }}
                    onMouseDown={(e) => {
                      if (isSelected) {
                        startMove(e, index);
                      } else {
                        e.stopPropagation();
                        setSelectedIndex(index);
                      }
                    }}
                  >
                    <span style={{
                      position: 'absolute',
                      top: '-20px',
                      left: 0,
                      backgroundColor: '#f59e0b',
                      color: 'black',
                      fontSize: '0.65rem',
                      fontWeight: 'bold',
                      padding: '1px 6px',
                    }}>
                      {index + 1}
                    </span>

                    {/* Resize handles */}
                    {handles && handles.map(({ handle, style: handleStyle, cursor }) => (
                      <div
                        key={handle}
                        style={{
                          position: 'absolute',
                          width: handleSize,
                          height: handleSize,
                          backgroundColor: '#fbbf24',
                          border: '1px solid #000',
                          cursor,
                          ...handleStyle,
                        }}
                        onMouseDown={(e) => startResize(e, index, handle)}
                      />
                    ))}
                  </div>
                );
              })}

              {/* Current drawing rectangle */}
              {currentRect && dragMode === 'draw' && (
                <div
                  style={{
                    position: 'absolute',
                    pointerEvents: 'none',
                    zIndex: 30,
                    ...getRectStyle(currentRect),
                    border: '2px dashed #fbbf24',
                    background: 'rgba(251, 191, 36, 0.2)',
                  }}
                />
              )}
            </div>
            {labels.length > 0 && (
              <p style={{ fontSize: '0.7rem', marginTop: '4px', opacity: 0.5 }}>
                Draw to add. Click to select. Drag to move. Use handles to resize.
              </p>
            )}
          </div>

          {/* Labels Panel */}
          <div style={{ flex: '1 1 180px', minWidth: '180px' }}>
            {labels.length === 0 ? (
              <p style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                No labels yet. Draw rectangles on the image to create regions.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: '320px', overflowY: 'auto' }}>
                {labels.map((label, index) => {
                  const isSelected = selectedIndex === index;
                  const isHovered = hoveredIndex === index;
                  const isFirst = index === 0;
                  const isLast = index === labels.length - 1;

                  return (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '8px',
                        padding: '6px 8px',
                        border: '1px solid var(--color-base-300)',
                        backgroundColor: isSelected
                          ? 'var(--color-neutral)'
                          : isHovered
                            ? 'var(--color-base-300)'
                            : 'var(--color-base-200)',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                      }}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      onClick={() => setSelectedIndex(index)}
                    >
                      {/* Cropped thumbnail */}
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          flexShrink: 0,
                          overflow: 'hidden',
                          border: '1px solid var(--color-base-300)',
                          backgroundColor: 'var(--color-base-100)',
                        }}
                      >
                        <img
                          src={imageUrl}
                          alt={`Region ${index + 1}`}
                          style={getCroppedImageStyle(label.coordinates, 40)}
                          draggable={false}
                        />
                      </div>

                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '4px' }}>
                          <span
                            style={{
                              padding: '2px 6px',
                              fontSize: '10px',
                              fontWeight: 600,
                              color: '#000',
                              backgroundColor: '#f59e0b',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {index + 1}
                          </span>
                          <div style={{ flex: 1 }} />
                          {/* Reorder buttons */}
                          <button
                            type="button"
                            className="btn btn-ghost btn-xs"
                            style={{
                              padding: '2px 4px',
                              minHeight: 'unset',
                              height: 'auto',
                              opacity: isFirst ? 0.3 : 1,
                              color: isSelected ? 'var(--color-white)' : 'var(--color-base-content)',
                            }}
                            disabled={isFirst}
                            onClick={(e) => {
                              e.stopPropagation();
                              moveLabel(index, 'up');
                            }}
                            title="Move up"
                          >
                            <FontAwesomeIcon icon={faArrowUp} style={{ fontSize: '10px' }} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-xs"
                            style={{
                              padding: '2px 4px',
                              minHeight: 'unset',
                              height: 'auto',
                              opacity: isLast ? 0.3 : 1,
                              color: isSelected ? 'var(--color-white)' : 'var(--color-base-content)',
                            }}
                            disabled={isLast}
                            onClick={(e) => {
                              e.stopPropagation();
                              moveLabel(index, 'down');
                            }}
                            title="Move down"
                          >
                            <FontAwesomeIcon icon={faArrowDown} style={{ fontSize: '10px' }} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-xs text-error"
                            style={{
                              padding: '2px 4px',
                              minHeight: 'unset',
                              height: 'auto',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteLabel(index);
                            }}
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} style={{ fontSize: '10px' }} />
                          </button>
                        </div>
                        <input
                          type="text"
                          value={label.text}
                          onChange={(e) => updateLabelText(index, e.target.value)}
                          placeholder="Enter label text..."
                          className="input input-bordered input-xs w-full"
                          style={{
                            backgroundColor: 'var(--color-base-100)',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
